import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, clear, totalPrice, totalCount } =
    useCart();

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-foreground-950/40 z-[60]"
        onClick={closeCart}
        aria-hidden="true"
      />

      <aside className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-background-50 z-[70] flex flex-col shadow-2xl shadow-black/30">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
          <h2 className="font-heading font-bold text-lg text-foreground-50 flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500/10">
              <i className="ri-shopping-cart-2-line text-primary-400" />
            </span>
            Panier
            {totalCount > 0 && (
              <span className="text-sm font-semibold text-foreground-600">({totalCount})</span>
            )}
          </h2>
          <button
            onClick={closeCart}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-shopping-cart-2-line text-3xl text-foreground-600" />
              </div>
              <p className="text-foreground-600 mb-4">Votre panier est vide.</p>
              <Link
                to="/shop"
                onClick={closeCart}
                className="inline-block px-5 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
              >
                Découvrir la Boutique
              </Link>
            </div>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-200/60 shrink-0">
                  {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-foreground-600" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground-50 line-clamp-1">{item.title}</h3>
                  <p className="text-xs text-foreground-600 mt-0.5">{item.sellerName}</p>
                  <p className="text-sm font-semibold text-primary-400 mt-1">
                    {item.price.toLocaleString('fr-FR')} FCFA
                  </p>
                </div>

                <div className="flex flex-col items-end justify-between">
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                    aria-label="Retirer"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-50 hover:bg-background-200/60 transition-colors"
                      aria-label="Diminuer"
                    >
                      <i className="ri-subtract-line text-sm" />
                    </button>
                    <span className="text-sm font-medium text-foreground-50 w-5 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-50 hover:bg-background-200/60 transition-colors"
                      aria-label="Augmenter"
                    >
                      <i className="ri-add-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-background-200/70 px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-600">Total</span>
              <span className="font-heading font-bold text-lg text-foreground-50">
                {totalPrice.toLocaleString('fr-FR')} FCFA
              </span>
            </div>
            <button
              onClick={clear}
              className="w-full text-xs text-foreground-600 hover:text-red-400 transition-colors"
            >
              Vider le panier
            </button>
            <p className="text-xs text-foreground-700 text-center leading-relaxed">
              Le paiement en ligne arrive bientôt. Contactez le vendeur pour finaliser votre achat.
            </p>
          </div>
        )}
      </aside>
    </>
  );
}

export default CartDrawer;